// MainLayout — корневой shell.
// Композирует: Sidebar (desktop) + Topbar + Main + BottomNav (mobile) + Drawer + SidePanel.
// Push-mechanics: main marginLeft = sidebarWidth, marginRight = sidePanelWidth.
// См. docs/ui-spec/_layout.{desktop,mobile}.md.
import { useRouterState } from "@tanstack/react-router";
import { forwardRef, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ActionDock } from "@/components/dnd/action-dock";
import { ArtifactCopyToasts } from "@/components/dnd/artifact-copy-toasts";
import { CollectionPickerDialog } from "@/components/dnd/collection-picker-dialog";
import { DnDProvider } from "@/components/dnd/dnd-provider";
import { AddArtifactToCollectionModal } from "@/components/library/add-artifact-to-collection-modal";
import { BottomNav } from "@/components/navigation/bottom-nav";
import { adaptNotification } from "@/components/notifications/notification-adapter";
import type { NotificationItemData } from "@/components/notifications/notification-item";
import { NotificationSheet } from "@/components/notifications/notification-sheet";
import { useKeyboardOpen } from "@/hooks/use-keyboard-open";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/cn";
import { useIsLgUp, useViewportWidth } from "@/lib/platform-detect";
import {
	useNotificationsListQuery,
	useUnreadNotificationsCountQuery,
} from "@/lib/powersync/queries/use-notifications-query";
import { useCourseWorkspaceStore } from "@/stores/course-workspace";
import { useDragSessionStore } from "@/stores/drag-session";
import { useSessionStore } from "@/stores/session";
import { useSidePanelStore } from "@/stores/side-panel";
import { useThemeStore } from "@/stores/theme";
import { isWorkspaceRoute, useMobileDrawerState, useSidebarLayoutState } from "./_layout-state";
import { REGULAR_CONTENT_COMFORT_WIDTH, useShellGeometry } from "./_panel-geometry";
import { AppPullToRefresh } from "./app-pull-to-refresh";
import { AudioMiniPlayer } from "./audio-mini-player";
import { MobileDrawer } from "./mobile-drawer";
import { usePanelResizeActive } from "./panel-resize-handle";
import { SidePanel } from "./side-panel";
import { Sidebar } from "./sidebar";
import { Topbar } from "./topbar";

interface MainLayoutProps {
	children: ReactNode;
}

/**
 * Полноэкранный чат `/tutor/<sessionId>` — на mobile прячем нижнее меню, чтобы
 * композер был у самого низа экрана (иммерсивный чат, как в tutor side-panel).
 * Исключения под /tutor/: список, scenarios/skills (конфиг) и /tutor/me (голос).
 */
function isFullBleedChatRoute(pathname: string): boolean {
	if (!pathname.startsWith("/tutor/")) return false;
	if (pathname === "/tutor/scenarios" || pathname === "/tutor/skills") return false;
	if (pathname.startsWith("/voice-tutor")) return false;
	return true;
}

export const MainLayout = forwardRef<HTMLElement, MainLayoutProps>(function MainLayout(
	{ children },
	ref,
) {
	const { t } = useTranslation("layout");
	const isDesktop = useIsLgUp();
	// Адрес ОТРИСОВАННОГО маршрута, а не запрошенного.
	//
	// `s.location` меняется в момент начала перехода — то есть ДО того, как
	// новый экран отрисован, и до того, как браузер снимает снимок уходящего
	// экрана. Каркас (шапка, нижняя навигация, отступы) переключался по нему
	// мгновенно, а содержимое приезжало на кадр позже. Наружу это выглядело как
	// две поломки сразу: вход в курс мигал списком без шапки, а выход показывал
	// одновременно нижнюю панель курса и нижнюю навигацию приложения — «два
	// футера», которые пользователь и сфотографировал.
	//
	// Каркас следует за тем экраном, который СЕЙЧАС НА ЭКРАНЕ, а не за
	// запрошенным адресом.
	//
	// `s.location` меняется в момент начала перехода — раньше, чем отрисован
	// новый экран, и раньше, чем браузер снимает снимок уходящего. Каркас по
	// нему переключался мгновенно, содержимое приезжало кадром позже: вход в
	// курс мигал списком без шапки, выход показывал нижнюю панель курса и
	// нижнюю навигацию приложения одновременно («два футера»).
	//
	// Порядок источников:
	// Источник — `s.matches`: набор совпадений, которые роутер отрисовывает
	// сейчас. Он меняется тем же кадром, что и содержимое, поэтому каркас и
	// экран переключаются вместе. `s.resolvedLocation` для этого не годится —
	// он обновляется уже ПОСЛЕ перехода, и остаётся окно, где экран курса
	// нарисован, а шапка с нижней навигацией ещё принадлежат приложению.
	const pathname = useRouterState({
		select: (s) => s.matches.at(-1)?.pathname ?? s.location.pathname,
	});
	// Режим воркспейса курса (§4.2): рельс приложения принудительно свёрнут,
	// глобальная панель репетитора подавлена (её роль играет панель курса),
	// BottomNav скрыт, <main> отдаёт ровно высоту экрана без страничного скролла.
	// Предикат — единственный на приложение (_layout-state.ts, декрет E8).
	const isWorkspace = isWorkspaceRoute(pathname);
	// Второй флаг — по ЗАПРОШЕННОМУ адресу, а не по отрисованному.
	//
	// Он гасит шапку и нижнюю навигацию сразу по тапу, ещё до того как экран
	// курса отрисован. Иначе накрывающее окно доезжало, анимация заканчивалась,
	// и каркас приложения ВОЗВРАЩАЛСЯ поверх скелетона курса до конца загрузки
	// его чанка — та самая «шапка скрылась через секунду».
	//
	// Геометрия (`isWorkspace` выше) при этом остаётся на отрисованном
	// маршруте: если убрать отступ под шапку тем же кадром, снимок уходящего
	// списка подскакивает на высоту шапки, и это видно как рывок.
	const isWorkspaceRequested = useRouterState({
		select: (s) => isWorkspaceRoute(s.location.pathname),
	});
	// Принудительный collapse НЕ пишется в localStorage — при выходе из курса
	// рельс вернётся в то состояние, которое выбрал пользователь.
	const sidebar = useSidebarLayoutState(isWorkspace);
	const drawer = useMobileDrawerState();
	// Единый персистируемый источник темы (stores/theme + ThemeProvider).
	// Раньше тут стоял локальный стаб useThemeLayoutState — он держал отдельный
	// несинхронизированный стейт и заново применял system-тему к <html> при
	// каждом mount, перетирая выбор из Settings и не переживая reload.
	const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
	const toggleTheme = useThemeStore((s) => s.toggle);

	const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
	// Активный drag карточки «Картотека» → на mobile прячем нижнее меню: Док
	// действий чисто занимает низ (без перекрытия меню), а навигация во время
	// перетаскивания всё равно не нужна. На drop меню возвращается.
	const isDragging = useDragSessionStore((s) => s.isDragging);
	// Открытая экранная клавиатура в приложении → нижнее меню прячем, как в
	// нативных приложениях. WebView там сжимается на высоту клавиатуры, и меню
	// вставало прямо над ней, отнимая ~64pt у и без того короткого экрана: в
	// мастере курса поле ввода оставалось в щели между шапкой и панелью
	// «Назад / Далее». В браузере признак не выставляется (см. хук).
	const keyboardOpen = useKeyboardOpen();
	// BottomNav показываем на mobile авторизованным, КРОМЕ полноэкранного чата
	// (там композер прижат к низу), воркспейса курса (его место занимает полоса
	// вызова панелей, §7.2) и активного drag. --bottom-nav-height синхронизирует
	// h-fit-app и нижние отступы с фактическим наличием меню; в воркспейсе она
	// становится 0, а собственную высоту полосы вызова воркспейс переопределяет
	// на своём корне.
	// Рисуем по ЗАПРОШЕННОМУ адресу (см. `isWorkspaceRequested`), а высоту
	// `--bottom-nav-height` ниже держим по отрисованному — иначе нижний отступ
	// уходящего списка схлопывается тем же кадром и снимок дёргается.
	const showBottomNav =
		!isDesktop &&
		isAuthenticated &&
		!isFullBleedChatRoute(pathname) &&
		!isWorkspace &&
		!isWorkspaceRequested &&
		!isDragging &&
		!keyboardOpen;
	const sidePanelOpen = useSidePanelStore((s) => s.open);
	const sidePanelKind = useSidePanelStore((s) => s.kind);
	const closeSidePanel = useSidePanelStore((s) => s.closePanel);
	const toggleSidePanel = useSidePanelStore((s) => s.toggle);
	// Репетитор в воркспейсе БОЛЬШЕ НЕ ПОДАВЛЯЕТСЯ (этап 1 плана единого
	// репетитора): панель смонтирована и там, поэтому отжимает <main> так же, как
	// на любой странице. Каркас курса (`WorkspaceShell`) от этого ужимается по
	// ширине — центр сцены `min-w-0 flex-1`, а сам shell `overflow-hidden`, —
	// а не получает горизонтальный скролл.
	const effectivePanel = sidePanelOpen ? sidePanelKind : null;

	// Mobile: центр уведомлений = bottom-sheet. PowerSync stream грузится
	// только пока sheet открыт (на desktop тот же поток читает SidePanel).
	const notificationsOpen = effectivePanel === "notifications";
	const { user } = useSession();
	const { data: notificationRows, isLoading: notificationsLoading } = useNotificationsListQuery(
		!isDesktop && notificationsOpen ? user?.id : undefined,
	);
	const notificationItems: NotificationItemData[] = useMemo(
		() => (notificationRows ?? []).map(adaptNotification),
		[notificationRows],
	);

	// Бейдж-счётчик колокольчика — реактивный COUNT поверх PowerSync stream.
	const { data: unreadRows } = useUnreadNotificationsCountQuery(user?.id);
	const unreadNotifications = unreadRows?.[0]?.count ?? 0;

	// Закрывать mobile drawer при переходе на desktop, чтобы не оставался открытым.
	useEffect(() => {
		if (isDesktop && drawer.isOpen) drawer.close();
	}, [isDesktop, drawer]);

	// Высота топбара в CSS-переменной — единый источник для BannerStack, `.h-fit-app`
	// и paddingTop `<main>`. В воркспейсе курса топбара нет вовсе (см. `showTopbar`),
	// поэтому переменная обязана стать 0: иначе экран курса вычтет высоту шапки,
	// которой на нём нет, и снизу останется пустая полоса.
	const topbarHeight = isWorkspace
		? "0px"
		: isDesktop
			? "calc(62px + env(safe-area-inset-top, 0px))"
			: "calc(56px + env(safe-area-inset-top, 0px))";
	// Воркспейс курса — полноэкранный обучающий центр, как прохождение теста или
	// карточек (те живут overlay-роутами вовсе без оболочки). Свою шапку он рисует
	// сам (название курса, освоение, «Сегодня», репетитор, ⋯), поэтому глобальный
	// топбар дублировал бы хром и съедал 62px высоты. Рельс приложения остаётся —
	// это единственный выход из курса на desktop.
	const showTopbar = !isWorkspace && !isWorkspaceRequested;

	// --topbar-height на <html> — чтобы BannerStack (рендерится в __root, вне
	// этого div) и любые fixed-элементы читали актуальную высоту топбара из
	// одного источника. На mobile включает safe-area-top (notch). На overlay-
	// routes MainLayout размонтирован → cleanup сбрасывает в 0px (CSS :root
	// default), баннеры/контент встают к верху экрана.
	// useLayoutEffect: до paint. Иначе первый кадр --topbar-height=0, и
	// BannerStack (z-20) садится под Topbar (z-chrome=30) — полоска синка
	// невидима ровно в тот момент, когда пользователь ждёт загрузку базы.
	useLayoutEffect(() => {
		document.documentElement.style.setProperty("--topbar-height", topbarHeight);
		return () => {
			document.documentElement.style.setProperty("--topbar-height", "0px");
		};
	}, [topbarHeight]);

	// Отметка режима на <html> — по ней styles.css снимает `scrollbar-gutter`
	// с документа и клипает overflow-y: в воркспейсе документ прокрутиться не
	// может (<main> ровно h-dvh, каркас bounded), зарезервированный жёлоб
	// читался как пустая полоса у правого края; без клипа краткий overflow на
	// загрузке дёргал весь UI на ширину скроллбара. useLayoutEffect — до paint,
	// иначе один кадр со stable-жёлобом → сдвиг. Атрибут, не класс: класс на
	// <html> ведёт ThemeProvider.
	useLayoutEffect(() => {
		if (!isWorkspace) return undefined;
		const html = document.documentElement;
		html.setAttribute("data-shell", "workspace");
		// Inline поверх styles.css: иначе на части движков `html { scrollbar-gutter:
		// stable }` продолжает резервировать жёлоб для fixed `right:0` (tutor
		// panel) → белая полоса справа от панели на всю высоту.
		html.style.setProperty("scrollbar-gutter", "auto");
		html.style.setProperty("overflow-y", "clip");
		return () => {
			html.removeAttribute("data-shell");
			html.style.removeProperty("scrollbar-gutter");
			html.style.removeProperty("overflow-y");
		};
	}, [isWorkspace]);

	const sidebarWidth = isDesktop ? (sidebar.isCollapsed ? 64 : 248) : 0;
	// Уведомления и репетитор — одинаковая ширина (паритет панелей), общий стор
	// + ресайз мышью за левую границу панели. Фактическая ширина — из общей
	// геометрии оболочки: на узком desktop панель ужимается (до 320px), вместо
	// того чтобы схлопывать колонки курса. Панель и push контента ОБЯЗАНЫ читать
	// одно и то же число — отсюда.
	const geometry = useShellGeometry();
	const sidePanelWidth = effectivePanel !== null ? geometry.panelWidth : 0;
	// Во время drag ручки ресайза гасим layout-переходы: иначе <main> и панели
	// «резиново» отстают от курсора на 200 мс (transition-[margin,padding]).
	const isResizing = usePanelResizeActive();

	// Авто-сворачивание сайдбара под широкую правую панель. Когда пользователь
	// тянет панель шире, чем влезает при развёрнутом сайдбаре (центру осталось бы
	// < MIN_CONTENT_WIDTH), сворачиваем сайдбар — освобождаем ~184px центру, чтобы
	// экраны не «разваливались». При сужении/закрытии панели разворачиваем обратно
	// (только если свернули МЫ). Реагируем на ПЕРЕХОД tooWide false↔true — ручной
	// toggle сайдбара пользователем не перебиваем.
	const viewportWidth = useViewportWidth();

	const setSidebarCollapsed = sidebar.setCollapsed;
	const sidebarCollapsedRef = useRef(sidebar.isCollapsed);
	sidebarCollapsedRef.current = sidebar.isCollapsed;
	const autoCollapsedRef = useRef(false);
	const prevTooWideRef = useRef(false);
	useEffect(() => {
		// В воркспейсе рельс и так принудительно свёрнут, а setCollapsed — no-op:
		// не трогаем ref-память «свернули МЫ», иначе после выхода из курса
		// авто-разворот не отработал бы.
		if (isWorkspace) return;
		if (!isDesktop || effectivePanel === null) {
			if (autoCollapsedRef.current) {
				setSidebarCollapsed(false);
				autoCollapsedRef.current = false;
			}
			prevTooWideRef.current = false;
			return;
		}
		const EXPANDED_SIDEBAR = 248;
		// Сравниваем с ФАКТИЧЕСКОЙ шириной панели: она сама ужимается на узком
		// окне, и мерить по предпочтению значило бы сворачивать рельс раньше нужного.
		const tooWide =
			viewportWidth - EXPANDED_SIDEBAR - sidePanelWidth < REGULAR_CONTENT_COMFORT_WIDTH;
		if (tooWide === prevTooWideRef.current) return;
		prevTooWideRef.current = tooWide;
		if (tooWide) {
			if (!sidebarCollapsedRef.current) {
				setSidebarCollapsed(true);
				autoCollapsedRef.current = true;
			}
		} else if (autoCollapsedRef.current) {
			setSidebarCollapsed(false);
			autoCollapsedRef.current = false;
		}
	}, [isWorkspace, isDesktop, effectivePanel, sidePanelWidth, viewportWidth, setSidebarCollapsed]);

	// Геометрия панелей курса. Стору отдаём ДОСТУПНУЮ ширину (окно за вычетом
	// открытой панели репетитора — она отжимает каркас целиком через marginRight
	// у `<main>`) и фактическую ширину колонки, посчитанную общей геометрией.
	//
	// Разделение ответственности: стор решает ТОЛЬКО «влезает ли панель вообще»
	// (и сворачивает, если нет), а сколько именно пикселей ей досталось — решает
	// `useShellGeometry`, потому что торг идёт между тремя колонками сразу.
	useEffect(() => {
		if (!isWorkspace || !isDesktop) return;
		useCourseWorkspaceStore.getState().applyViewport(viewportWidth - sidePanelWidth);
	}, [isWorkspace, isDesktop, viewportWidth, sidePanelWidth]);

	// Дублируем геометрию sidebar / правой панели в CSS-переменные на <html> —
	// Toaster (sonner) рендерится порталом на уровне <body> и НЕ видит переменные,
	// заданные на <main> ниже. Тостер центрируется по контентной области (между
	// sidebar и правой панелью) через toast.css. На mobile/overlay = 0 → центр
	// экрана. Cleanup на размонтировании (overlay-routes) сбрасывает в 0.
	useEffect(() => {
		const root = document.documentElement;
		root.style.setProperty("--sidebar-width", `${sidebarWidth}px`);
		root.style.setProperty("--side-panel-width", `${sidePanelWidth}px`);
		return () => {
			root.style.setProperty("--sidebar-width", "0px");
			root.style.setProperty("--side-panel-width", "0px");
		};
	}, [sidebarWidth, sidePanelWidth]);

	return (
		<DnDProvider>
			<div
				className={cn(
					"bg-background text-foreground",
					// Воркспейс: ровно экран + clip — никакой in-flow потомок не
					// раздует document и не покажет viewport-скроллбар (CLS на
					// загрузке). Обычные страницы — min-h-dvh, документ скроллится.
					isWorkspace ? "h-dvh overflow-hidden" : "min-h-dvh",
				)}
				// CSS-переменные для fixed-элементов (AudioMiniPlayer и др.),
				// которым нужно учитывать ширину sidebar / правой side-panel.
				// --topbar-height — единый источник истины высоты Topbar (desktop 62px /
				// mobile 56px). «Рабочие панели» (workbench), которые должны вписываться
				// в экран без вертикального скролла, считают свою высоту как
				// `calc(100dvh - var(--topbar-height) - var(--audio-player-height,0px))`,
				// а не магическими числами. Совпадает с pt-[62px]/pt-14 на <main>.
				style={
					{
						"--sidebar-width": `${sidebarWidth}px`,
						"--side-panel-width": `${sidePanelWidth}px`,
						// Mobile топбар включает safe-area-top (notch в Capacitor) — bounded
						// fit-to-screen страницы вычитают именно это значение из 100dvh.
						// В воркспейсе курса топбара нет → 0px (см. `topbarHeight`).
						"--topbar-height": topbarHeight,
						// Высота нижнего меню (4rem когда показано, 0 когда скрыто —
						// desktop/гость/полноэкранный чат). h-fit-app и paddingBottom
						// ниже читают эту переменную.
						// Высота — по ОТРИСОВАННОМУ маршруту (`isWorkspace`), а не по
						// факту показа меню: `showBottomNav` гаснет раньше, чтобы каркас
						// не всплыл поверх накрывающего экрана, и привязка высоты к нему
						// дёргала бы нижний отступ уходящего списка.
						// При открытой клавиатуре меню скрыто → высота 0, иначе липкие
						// панели (футер мастера, MobileActionBar) висели бы на 4rem выше
						// клавиатуры над пустотой.
						"--bottom-nav-height":
							!isDesktop &&
							isAuthenticated &&
							!isFullBleedChatRoute(pathname) &&
							!isWorkspace &&
							!keyboardOpen
								? "4rem"
								: "0px",
					} as React.CSSProperties
				}
			>
				{/* Skip link для клавиатуры / screen reader */}
				<a
					href="#main-content"
					className={cn(
						"sr-only focus:not-sr-only",
						"focus:fixed focus:top-2 focus:left-2 focus:z-(--sh-z-overlay)",
						"focus:bg-card focus:text-foreground focus:border focus:border-border",
						"focus:rounded-[10px] focus:px-3 focus:py-1.5 focus:text-sm focus:shadow-[var(--shadow-e2)]",
					)}
				>
					{t("a11y.skipToContent")}
				</a>

				{isDesktop && (
					<Sidebar
						isCollapsed={sidebar.isCollapsed}
						onToggleCollapsed={sidebar.toggle}
						// В воркспейсе рельс принудительно свёрнут, а `sidebar.toggle` —
						// no-op (useSidebarLayoutState(forceCollapsed)). Без этого пропа
						// кнопка «Развернуть» осталась бы кликабельной и молча ничего не
						// делала — тот же дефект, что закрыт скрытием TutorPanelTrigger.
						canToggleCollapsed={!isWorkspace}
					/>
				)}

				{showTopbar && (
					<Topbar
						sidebarWidth={sidebarWidth}
						sidePanelWidth={sidePanelWidth}
						isDesktop={isDesktop}
						isAuthenticated={isAuthenticated}
						onOpenMobileDrawer={drawer.open}
						onToggleTheme={toggleTheme}
						resolvedTheme={resolvedTheme}
						onToggleNotifications={() => toggleSidePanel("notifications")}
						unreadNotifications={unreadNotifications}
					/>
				)}

				<main
					ref={ref}
					id="main-content"
					className={cn(
						// overflow-x-clip — глобальный guard от горизонтального переполнения
						// (rules/frontend-ui.md §9.2): любой блок шире вьюпорта (мин-контент
						// карусели/grid без min-w-0) клипается, а не рвёт ширину страницы и
						// не режет текст. clip жёстче hidden — не создаёт scroll-контейнер.
						!isWorkspace && "min-h-dvh overflow-x-clip",
						// Воркспейс курса: ровно высота экрана. paddingTop/Bottom ниже
						// остаются (компенсируют fixed Topbar/баннеры и нижний хром), поэтому
						// content-box <main> в точности равен .h-fit-app:
						//   100dvh − topbar − banners − bottom-nav − safe-area − player.
						// Дети могут брать и h-full, и h-fit-app — значения совпадают, и
						// bounded-каркас воркспейса переполнения не даёт (скроллятся сцена
						// и панели внутри себя).
						//
						// По оси Y — `auto`, НЕ `hidden`. `overflow-hidden` здесь означал бы:
						// любой контент выше экрана становится физически недостижимым (нет
						// ни скроллбара, ни скролла документа — <main> ровно во весь экран).
						// `auto` — мягкая деградация (правило §13b): bounded-каркас
						// переполнения не создаёт и скроллбар не показывает, а если сцена
						// когда-нибудь перерастёт экран, её можно будет домотать.
						//
						// `scrollbar-stable` здесь НЕТ намеренно. Каркас воркспейса
						// bounded (скролл внутри сцены/панелей), жёлоб на <main> читался
						// как пустая полоса у правого края / рядом с tutor-панелью.
						// CLS от document-scrollbar гасится `data-shell=workspace` +
						// `h-dvh overflow-hidden` на оболочке выше — не gutter'ом <main>.
						isWorkspace && "h-dvh overflow-y-auto overflow-x-clip",
						!isResizing && "transition-[margin,padding] duration-base ease-out",
					)}
					style={{
						marginLeft: sidebarWidth,
						marginRight: sidePanelWidth,
						// padding-top = высота топбара (--topbar-height, с safe-area-top на
						// mobile) + высота стека баннеров (--banners-height, 0 когда баннеров
						// нет). Контент не уезжает ни под топбар, ни под баннеры.
						paddingTop: "calc(var(--topbar-height) + var(--banners-height, 0px))",
						// padding-bottom = bottom-nav + safe-area + audio player.
						// В воркспейсе курса BottomNav скрыт, а WorkspaceCallBar сам
						// красит safe-area (`pb-[env(...)]` + bg-card) — иначе под
						// полосой остаётся серый `--bg` Android gesture area.
						paddingBottom: isWorkspace
							? "calc(var(--audio-player-height, 0px))"
							: "calc(var(--bottom-nav-height, 0px) + env(safe-area-inset-bottom, 0px) + var(--audio-player-height, 0px))",
					}}
				>
					{/* «Потяни-чтобы-обновить» — ОДИН на приложение, здесь, а не в
					    каждом списке (components/layout/app-pull-to-refresh.tsx).
					    Выключен там, где прокрутка не страничная: воркспейс курса
					    (скролл внутри сцены и панелей) и полноэкранный чат (тяга
					    вверх ленты — это история сообщений, не обновление). */}
					<AppPullToRefresh disabled={isWorkspace || isFullBleedChatRoute(pathname)}>
						{children}
					</AppPullToRefresh>
				</main>

				{/* Tutor side-panel смонтирована глобально в __root.tsx (единый инстанс,
			    работает и на overlay-роутах: тест / карточки / голос). Push на обычных
			    routes делает marginRight на <main> ниже (sidePanelWidth), на overlay-
			    routes — сами контейнеры через --tutor-push-width. */}
				{/* SidePanel (notifications) на desktop всегда смонтирован для
			    translateX slide-in/out (кроме случая, когда открыт tutor). */}
				{isDesktop && effectivePanel !== "tutor" && (
					<SidePanel activePanel={effectivePanel} onClose={closeSidePanel} />
				)}

				{showBottomNav && <BottomNav onOpenMore={drawer.open} />}
				{/* Как NotificationSheet: не hard-unmount'ить открытый vaul Drawer. */}
				<MobileDrawer isOpen={!isDesktop && drawer.isOpen} onOpenChange={drawer.setOpen} />

				{/* Mobile: центр уведомлений — bottom-sheet. Держим смонтированным и
				    на desktop с open=false: hard-unmount открытой vaul-шторки при
				    пересечении lg роняет DialogPortal («must be used within Dialog»). */}
				<NotificationSheet
					open={!isDesktop && notificationsOpen}
					onOpenChange={(next) => {
						if (!next) closeSidePanel();
					}}
					items={notificationItems}
					isLoading={notificationsLoading}
				/>

				{/* Глобальный mini-player аудио-резюме. Сам решает когда рендериться
			    (recapId set + не на /audio-recaps/$id). */}
				<AudioMiniPlayer />

				{/* «Док действий» — выезжает снизу при drag карточки «Картотека».
			    В покое уведён за край и не перехватывает клики. */}
				<ActionDock />
				{/* Модал «весь материал vs независимая копия» — открывается при drop
			    bound-артефакта на чип коллекции (читает dragSession.pendingResolve). */}
				<AddArtifactToCollectionModal />
				{/* Пикер «Выбрать коллекцию…» — поиск по всем коллекциям при drop на
			    одноимённую цель дока (читает dragSession.pendingPick). */}
				<CollectionPickerDialog />
				{/* Глобальный Socket-финал async-копии артефакта (audio/visual S3-копия). */}
				<ArtifactCopyToasts />
			</div>
		</DnDProvider>
	);
});
